import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const colorMap: Record<string, { bg: string; text: string }> = {
  accent: { bg: 'bg-accent-100', text: 'text-accent-500' },
  primary: { bg: 'bg-primary-100', text: 'text-primary-500' },
  secondary: { bg: 'bg-secondary-100', text: 'text-secondary-500' },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);
  const colorClasses = colorMap[category?.color || 'primary'];

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/rexue' },
    { label: category.name },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name}漫画 - 073漫画网`,
            description: `${category.name}漫画分类，${category.description}`,
            url: `https://073manhua.com/category/${slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://073manhua.com' },
                { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://073manhua.com/category/rexue' },
                { '@type': 'ListItem', position: 3, name: `${category.name}漫画` },
              ],
            },
          }),
        }}
      />

      <div className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4 mb-10">
            <div className="flex items-center gap-4 mb-3">
              <span className={`w-12 h-12 rounded-xl ${colorClasses.bg} flex items-center justify-center`}>
                <i className={`${category.icon} text-xl ${colorClasses.text}`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}漫画</h1>
                <p className="text-foreground-500 text-sm mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group"
                  data-product-shop
                >
                  <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-accent-300 transition-all duration-300">
                    <div className="aspect-[3/4] overflow-hidden relative">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                          {comic.rating}
                        </span>
                      </div>
                      {comic.status === '连载中' && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 bg-secondary-500 text-white text-xs rounded-full whitespace-nowrap">
                            连载
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-foreground-500 text-xs mt-0.5">{comic.author}</p>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-background-200">
                        <span className="text-foreground-400 text-xs">
                          <i className="ri-eye-line mr-1"></i>{(comic.views / 10000).toFixed(0)}万
                        </span>
                        <span className="text-foreground-400 text-xs">{comic.chapters}话</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画作品</p>
              <Link to="/" className="text-primary-500 text-sm mt-2 inline-block">返回首页</Link>
            </div>
          )}

          <div className="mt-12 mb-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}